/**
 * Page Content Extractor
 *
 * Extracts the main content from a web page while filtering out
 * non-relevant elements like scripts, styles, and the PankAgent widget itself.
 */

/**
 * Configuration for content extraction
 */
export interface ExtractorOptions {
  /** Maximum content length in characters (default: 50000) */
  maxLength?: number;
  /** Include metadata like title and meta description (default: true) */
  includeMetadata?: boolean;
  /** CSS selectors to exclude (in addition to defaults) */
  excludeSelectors?: string[];
  /** CSS selector for main content area (optional, improves extraction) */
  mainContentSelector?: string;
}

const DEFAULT_OPTIONS: Required<ExtractorOptions> = {
  maxLength: 50000,
  includeMetadata: true,
  excludeSelectors: [],
  mainContentSelector: "",
};

/**
 * Default selectors for elements to exclude from extraction
 */
const DEFAULT_EXCLUDE_SELECTORS = [
  // PankAgent widget
  "#pankagent-widget",
  "[id^='pankagent']",
  // Scripts and styles
  "script",
  "style",
  "noscript",
  "template",
  // Media and embeds
  "iframe",
  "object",
  "embed",
  "svg",
  "canvas",
  "audio",
  "video",
  // Common UI elements to skip
  "[role='banner']",
  "[role='navigation']",
  "[role='complementary']",
  "[role='contentinfo']",
  // Common class patterns for navigation/footer
  ".nav",
  ".navbar",
  ".navigation",
  ".header-nav",
  ".footer",
  ".site-footer",
  ".cookie-banner",
  ".cookie-consent",
  ".popup",
  ".modal",
  ".overlay",
  ".advertisement",
  ".ad",
  ".ads",
  ".sidebar",
  ".widget:not([id^='pankagent'])",
  // Hidden elements
  "[hidden]",
  '[aria-hidden="true"]',
  '[style*="display: none"]',
  '[style*="display:none"]',
  '[style*="visibility: hidden"]',
  '[style*="visibility:hidden"]',
];

/**
 * Priority selectors for main content (checked in order)
 */
const MAIN_CONTENT_SELECTORS = [
  "main",
  '[role="main"]',
  "article",
  ".content",
  ".main-content",
  ".article-content",
  ".post-content",
  ".entry-content",
  "#content",
  "#main",
  "#main-content",
];

/**
 * Page metadata extracted from the document
 */
export interface PageMetadata {
  title: string;
  description: string;
  url: string;
  language: string;
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  keywords?: string[];
}

/**
 * Result of content extraction
 */
export interface ExtractedContent {
  /** The main HTML content (cleaned) */
  html: string;
  /** Page metadata */
  metadata: PageMetadata;
  /** Whether the content was truncated */
  truncated: boolean;
  /** Original content length before truncation */
  originalLength: number;
}

/**
 * Extract the page metadata from the document
 */
export function extractPageMetadata(doc: Document = document): PageMetadata {
  const getMetaContent = (name: string): string => {
    const meta =
      doc.querySelector(`meta[name="${name}"]`) ||
      doc.querySelector(`meta[property="${name}"]`) ||
      doc.querySelector(`meta[property="og:${name}"]`);
    return meta?.getAttribute("content") || "";
  };

  const keywords = getMetaContent("keywords");

  return {
    title: doc.title || "",
    description: getMetaContent("description"),
    url: doc.location?.href || window.location.href,
    language: doc.documentElement.lang || "en",
    author: getMetaContent("author"),
    publishedDate:
      getMetaContent("article:published_time") ||
      getMetaContent("datePublished"),
    modifiedDate:
      getMetaContent("article:modified_time") || getMetaContent("dateModified"),
    keywords: keywords ? keywords.split(",").map((k) => k.trim()) : undefined,
  };
}

/**
 * Find the main content container in the document
 */
function findMainContent(
  doc: Document,
  customSelector?: string
): Element | null {
  // Try custom selector first
  if (customSelector) {
    const custom = doc.querySelector(customSelector);
    if (custom) return custom;
  }

  // Try priority selectors
  for (const selector of MAIN_CONTENT_SELECTORS) {
    const element = doc.querySelector(selector);
    if (element) return element;
  }

  // Fall back to body
  return doc.body;
}

/**
 * Clone an element and remove excluded elements
 */
function cloneAndClean(element: Element, excludeSelectors: string[]): Element {
  // Clone the element to avoid modifying the original
  const clone = element.cloneNode(true) as Element;

  // Combine default and custom exclude selectors
  const allExcludeSelectors = [
    ...DEFAULT_EXCLUDE_SELECTORS,
    ...excludeSelectors,
  ];

  // Remove excluded elements
  for (const selector of allExcludeSelectors) {
    try {
      const elements = clone.querySelectorAll(selector);
      elements.forEach((el) => el.remove());
    } catch {
      // Invalid selector, skip
    }
  }

  // Remove elements with common "skip" patterns in class/id
  const skipPatterns =
    /^(ad|ads|advertisement|banner|cookie|popup|modal|overlay|sidebar|nav|menu|footer|header)$/i;

  clone.querySelectorAll("*").forEach((el) => {
    const id = el.id?.toLowerCase() || "";
    const classes = Array.from(el.classList).join(" ").toLowerCase();

    if (skipPatterns.test(id) || skipPatterns.test(classes)) {
      // Only remove if it's not a main content container
      if (!MAIN_CONTENT_SELECTORS.some((sel) => el.matches(sel))) {
        el.remove();
      }
    }
  });

  return clone;
}

/**
 * Extract content from the page
 */
export function extractPageContent(
  options: ExtractorOptions = {}
): ExtractedContent {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const doc = document;

  // Extract metadata
  const metadata = extractPageMetadata(doc);

  // Find main content area
  const mainContent = findMainContent(doc, opts.mainContentSelector);

  if (!mainContent) {
    return {
      html: "",
      metadata,
      truncated: false,
      originalLength: 0,
    };
  }

  // Clone and clean the content
  const cleanedContent = cloneAndClean(mainContent, opts.excludeSelectors);

  // Get the HTML
  let html = cleanedContent.innerHTML;
  const originalLength = html.length;

  // Truncate if necessary
  let truncated = false;
  if (html.length > opts.maxLength) {
    // Try to truncate at a paragraph boundary
    const truncateAt = html.lastIndexOf("</p>", opts.maxLength);
    if (truncateAt > opts.maxLength * 0.8) {
      html = html.substring(0, truncateAt + 4);
    } else {
      html = html.substring(0, opts.maxLength);
    }
    truncated = true;
  }

  return {
    html,
    metadata,
    truncated,
    originalLength,
  };
}

/**
 * Get the raw text content of the page (without any HTML)
 * Useful for simple text extraction without markdown conversion
 */
export function extractTextContent(options: ExtractorOptions = {}): string {
  const { html, metadata } = extractPageContent(options);

  // Create a temporary element to extract text
  const temp = document.createElement("div");
  temp.innerHTML = html;

  // Get text content
  let text = temp.textContent || "";

  // Add metadata as header if enabled
  if (options.includeMetadata !== false && metadata.title) {
    const header = [
      `# ${metadata.title}`,
      metadata.description ? `\n${metadata.description}` : "",
      `\nURL: ${metadata.url}`,
    ]
      .filter(Boolean)
      .join("");

    text = `${header}\n\n---\n\n${text}`;
  }

  // Clean up whitespace
  return text.replace(/\s+/g, " ").trim();
}
